
import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { axiosInstance } from "../../utility/apiInstance";

interface Attachment {
    url: string;
    key: string;
    name: string;
}

interface Comment {
    _id: string;
    milestoneId: string;
    text: string;
    attachments: Attachment[];
    authorName: string;
    author: string;
    type: string;
    createdAt: string;
}

interface MilestoneCommentsProps {
    comments: Comment[];
    milestoneName: string;
}

function formatCommentDate(dateStr: string): string {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h12",
    });
}

function isImageUrl(url: string): boolean {
    return /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(url);
}

async function downloadFile(url: string, filename: string) {
    try {
        const response = await axiosInstance.get(`/api/v1/s3/proxy-download`, {
            params: { url },
            responseType: "blob",
        });

        const blobUrl = URL.createObjectURL(response.data);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
    } catch (err) {
        console.error("Download error:", err);
    }
}

export function MilestoneComments({ comments, milestoneName }: MilestoneCommentsProps) {
    const [open, setOpen] = useState(false);
    const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
    const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({});
    const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (open && wrapperRef.current) {
            const rect = wrapperRef.current.getBoundingClientRect();
            const popupWidth = 270;
            const gap = 8;

            let left = rect.right + gap;
            if (left + popupWidth > window.innerWidth - 10) {
                left = rect.left - popupWidth - gap;
            }

            let top = rect.top + rect.height / 2 - 60;
            top = Math.max(10, Math.min(top, window.innerHeight - 320));

            setPopupStyle({
                position: "fixed",
                top,
                left,
                zIndex: 99999,
                width: `${popupWidth}px`,
            });
        }
    }, [open]);

    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            const target = e.target as Node;
            const clickedInsideWrapper = wrapperRef.current?.contains(target);
            const clickedInsidePopup = popupRef.current?.contains(target);
            if (!clickedInsideWrapper && !clickedInsidePopup) {
                setOpen(false);
            }
        }
        if (open) document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open]);

    if (!comments || comments.length === 0) return null;

    const handleDownload = async (e: React.MouseEvent, url: string, name: string) => {
        e.preventDefault();
        e.stopPropagation();
        setDownloadingKey(url);
        await downloadFile(url, name);
        setDownloadingKey(null);
    };

    const popup = open ? (
        <div
            ref={popupRef}
            onClick={(e) => e.stopPropagation()}
            style={{
                ...popupStyle,
                background: "#fff",
                borderRadius: "10px",
                boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                border: "1px solid #e5e7eb",
                display: "flex",
                flexDirection: "column",
            }}
        >
            {/* Header */}
            <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 12px 8px",
                borderBottom: "1px solid #f3f4f6",
                background: "#fafafa",
                borderRadius: "10px 10px 0 0",
                flexShrink: 0,
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <ChatBubbleOutlineIcon style={{ fontSize: "14px", color: "#d97706" }} />
                    <span style={{ fontSize: "12px", fontWeight: 700, color: "#111827" }}>Comments</span>
                    <span style={{
                        fontSize: "10px",
                        background: "#fef3c7",
                        color: "#92400e",
                        borderRadius: "10px",
                        padding: "1px 6px",
                        fontWeight: 600,
                    }}>
                        {comments.length}
                    </span>
                </div>
                <button
                    onClick={() => setOpen(false)}
                    style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#9ca3af",
                        padding: "2px",
                        display: "flex",
                    }}
                >
                    <CloseIcon style={{ fontSize: "15px" }} />
                </button>
            </div>

            {/* Milestone name */}
            <div style={{
                padding: "6px 12px",
                fontSize: "11px",
                color: "#6b7280",
                borderBottom: "1px solid #f3f4f6",
                background: "#fff",
                flexShrink: 0,
            }}>
                {milestoneName}
            </div>

            {/* Scrollable comments list */}
            <div style={{
                maxHeight: "260px",
                overflowY: "auto",
                borderRadius: "0 0 10px 10px",
            }}>
                {comments.map((comment, idx) => (
                    <div
                        key={comment._id}
                        style={{
                            padding: "10px 12px",
                            borderBottom: idx < comments.length - 1 ? "1px solid #f3f4f6" : "none",
                        }}
                    >
                        {/* Time + Author row */}
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "5px",
                            gap: "6px",
                        }}>
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                color: "#9ca3af",
                                fontSize: "10px",
                                flexShrink: 0,
                            }}>
                                <AccessTimeIcon style={{ fontSize: "11px" }} />
                                {formatCommentDate(comment.createdAt)}
                            </div>
                            <span style={{
                                fontSize: "11px",
                                fontWeight: 600,
                                color: "#374151",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "100px",
                            }}>
                                {comment.authorName}
                            </span>
                        </div>

                        {/* Comment text */}
                        {comment.text && (
                            <p style={{
                                fontSize: "12px",
                                color: "#1f2937",
                                margin: "0 0 6px 0",
                                lineHeight: "1.55",
                                wordBreak: "break-word",
                            }}>
                                {comment.text}
                            </p>
                        )}

                        {/* Attachments */}
                        {comment.attachments?.length > 0 && (
                            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                {comment.attachments.map((att) => (
                                    <div
                                        key={att.key}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                            background: "#f8fafc",
                                            border: "1px solid #e2e8f0",
                                            borderRadius: "6px",
                                            padding: "4px 6px",
                                        }}
                                    >
                                        <InsertDriveFileIcon style={{ fontSize: "13px", color: "#3b82f6", flexShrink: 0 }} />

                                        {/* View / lightbox link */}
                                        {isImageUrl(att.url) ? (
                                            <button
                                                onClick={() => setLightboxUrl(att.url)}
                                                style={{
                                                    background: "none",
                                                    border: "none",
                                                    padding: 0,
                                                    cursor: "pointer",
                                                    color: "#2563eb",
                                                    fontSize: "11px",
                                                    textAlign: "left",
                                                    flex: 1,
                                                    minWidth: 0,
                                                }}
                                            >
                                                <span style={{
                                                    textDecoration: "underline",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                    display: "block",
                                                }}>
                                                    {att.name}
                                                </span>
                                            </button>
                                        ) : (
                                            <a
                                                href={att.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    color: "#2563eb",
                                                    fontSize: "11px",
                                                    textDecoration: "none",
                                                    flex: 1,
                                                    minWidth: 0,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                <span style={{ textDecoration: "underline" }}>{att.name}</span>
                                            </a>
                                        )}

                                        {/* Download button */}
                                        <button
                                            onClick={(e) => handleDownload(e, att.url, att.name)}
                                            title="Download file"
                                            disabled={downloadingKey === att.url}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                flexShrink: 0,
                                                color: downloadingKey === att.url ? "#d1d5db" : "#6b7280",
                                                padding: "2px",
                                                borderRadius: "4px",
                                                transition: "color 0.15s",
                                                background: "none",
                                                border: "none",
                                                cursor: downloadingKey === att.url ? "not-allowed" : "pointer",
                                            }}
                                            onMouseEnter={(e) => {
                                                if (downloadingKey !== att.url)
                                                    e.currentTarget.style.color = "#2563eb";
                                            }}
                                            onMouseLeave={(e) => {
                                                if (downloadingKey !== att.url)
                                                    e.currentTarget.style.color = "#6b7280";
                                            }}
                                        >
                                            {downloadingKey === att.url ? (
                                                // Spinner while downloading
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="13"
                                                    height="13"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2.2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    style={{ animation: "spin 1s linear infinite" }}
                                                >
                                                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                                </svg>
                                            ) : (
                                                // Download icon
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="13"
                                                    height="13"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2.2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                    <polyline points="7 10 12 15 17 10" />
                                                    <line x1="12" y1="15" x2="12" y2="3" />
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    ) : null;

    return (
        <>
            {/* Spin keyframe */}
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

            <div
                ref={wrapperRef}
                style={{ position: "relative", display: "inline-flex", verticalAlign: "middle" }}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={() => setOpen((p) => !p)}
                    title={`${comments.length} comment${comments.length > 1 ? "s" : ""}`}
                    style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "2px 3px",
                        display: "inline-flex",
                        alignItems: "center",
                        position: "relative",
                        color: "#f59e0b",
                        marginLeft: "4px",
                    }}
                >
                    <ChatBubbleOutlineIcon style={{ fontSize: "16px" }} />
                    <span style={{
                        position: "absolute",
                        top: "-4px",
                        right: "-5px",
                        background: "#ef4444",
                        color: "#fff",
                        borderRadius: "50%",
                        fontSize: "8px",
                        fontWeight: 700,
                        minWidth: "13px",
                        height: "13px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        lineHeight: 1,
                    }}>
                        {comments.length}
                    </span>
                </button>
            </div>

            {typeof document !== "undefined" && ReactDOM.createPortal(popup, document.body)}

            {/* Image lightbox */}
            {lightboxUrl && typeof document !== "undefined" && ReactDOM.createPortal(
                <div
                    onClick={() => setLightboxUrl(null)}
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 999999,
                        background: "rgba(0,0,0,0.8)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <button
                        onClick={() => setLightboxUrl(null)}
                        style={{
                            position: "absolute",
                            top: "20px",
                            right: "20px",
                            background: "rgba(255,255,255,0.15)",
                            border: "none",
                            borderRadius: "50%",
                            cursor: "pointer",
                            color: "#fff",
                            width: "36px",
                            height: "36px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <CloseIcon style={{ fontSize: "20px" }} />
                    </button>
                    <img
                        src={lightboxUrl}
                        alt="attachment"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            maxWidth: "90vw",
                            maxHeight: "85vh",
                            borderRadius: "8px",
                            boxShadow: "0 4px 32px rgba(0,0,0,0.5)",
                        }}
                    />
                </div>,
                document.body
            )}
        </>
    );
}